import React, { useState } from "react"

import * as Sentry from "@sentry/react"
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { CreditCard, Lock } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useApplication } from "@/contexts/ApplicationContext"
import { useToast } from "@/hooks/use-toast"
import supabase from "@/supabase"
import { getFunctionsErrorMessage } from "@/utils/supabaseFunctionsError"

interface VehicleBookingProps {
  onComplete?: () => void
}

const VehicleBooking: React.FC<VehicleBookingProps> = ({ onComplete }) => {
  const { application, refetch } = useApplication()
  const { toast } = useToast()
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)

  const createSetupIntent = async (applicationId: string) => {
    const { data: setupIntentData, error: setupIntentError } = await supabase.functions.invoke(
      "process-vehicle-booking-payment/setup-intent",
      {
        body: {
          applicationId,
        },
      },
    )

    if (setupIntentError) {
      const message = await getFunctionsErrorMessage(
        setupIntentError,
        "Failed to start payment verification",
      )
      throw new Error(message)
    }

    if (!setupIntentData?.success) {
      throw new Error(setupIntentData?.error || "Failed to start payment verification")
    }

    if (!setupIntentData?.clientSecret) {
      throw new Error(setupIntentData?.error || "Failed to start payment verification")
    }

    return setupIntentData.clientSecret as string
  }

  const attachPaymentMethod = async (applicationId: string, paymentMethodId: string) => {
    const { data: functionData, error: functionError } = await supabase.functions.invoke(
      "process-vehicle-booking-payment/attach-payment-method",
      {
        body: {
          applicationId,
          paymentMethodId,
        },
      },
    )

    if (functionError) {
      const message = await getFunctionsErrorMessage(functionError, "Payment processing failed")
      throw new Error(message)
    }

    if (!functionData?.success) {
      throw new Error(functionData?.error || "Payment processing failed")
    }

    if (!functionData?.stripeCustomerId || !functionData?.paymentMethodId) {
      throw new Error("Payment method was not properly saved. Please try again.")
    }

    return functionData
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      toast({
        title: "Payment System Not Ready",
        description: "Please wait a moment and try again.",
        variant: "destructive",
      })
      return
    }

    if (!application?.id || !application?.applicationId) {
      toast({
        title: "Application Error",
        description: "Unable to process payment. Please refresh and try again.",
        variant: "destructive",
      })
      return
    }

    setIsProcessing(true)
    let paymentMethodId: string | undefined
    let setupIntentId: string | undefined

    try {
      const cardNumberElement = elements.getElement(CardNumberElement)

      if (!cardNumberElement) {
        throw new Error("Card element not found")
      }

      const clientSecret = await createSetupIntent(application.applicationId)

      // Confirm SetupIntent on the client to validate the card (3DS if needed)
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: `${application.basicDetails.firstName} ${application.basicDetails.lastName}`,
            email: application.basicDetails.email,
            phone: application.basicDetails.phoneNumber,
            address: {
              line1: application.personalDetails.address.line1,
              city: application.personalDetails.address.city,
              postal_code: application.personalDetails.address.postcode,
              country: "UK",
            },
          },
        },
      })
      if (confirmError) {
        throw new Error(confirmError.message)
      }

      if (!setupIntent || setupIntent.status !== "succeeded") {
        const status = setupIntent?.status
        let message = "Card verification was not completed."
        if (status === "requires_action") {
          message = "Card authentication was not completed."
        } else if (status === "requires_payment_method") {
          message = "Card verification failed. Please check your details or use another card."
        } else if (status === "processing") {
          message = "Card verification is still processing. Please try again."
        } else if (status === "canceled") {
          message = "Card verification was canceled."
        }
        throw new Error(message)
      }

      if (!setupIntent.payment_method || typeof setupIntent.payment_method !== "string") {
        throw new Error("Failed to verify payment method")
      }

      setupIntentId = setupIntent.id
      paymentMethodId = setupIntent.payment_method

      const functionData = await attachPaymentMethod(application.applicationId, paymentMethodId)

      console.log("Cloud function response:", functionData)

      // Update local application state and verify it was saved
      await refetch()

      console.log("Refetched application vehicleBooking:", application.vehicleBooking)

      // Double-check that the payment method was actually saved to the database
      // Note: We check against the cloud function response since refetch may not have updated yet
      if (functionData.paymentMethodId !== paymentMethodId) {
        throw new Error("Payment method ID mismatch")
      }

      toast({
        title: "Payment Method Saved",
        description: "Your payment information has been securely saved.",
      })

      // Call onComplete callback if provided
      if (onComplete) {
        onComplete()
      }
    } catch (error) {
      console.error("Payment error:", error)

      // Capture error in Sentry with context
      Sentry.captureException(error, {
        tags: {
          component: "VehicleBooking",
          action: "payment_submission",
        },
        extra: {
          applicationId: application?.applicationId,
          paymentMethodId: paymentMethodId,
          setupIntentId: setupIntentId,
          stripeReady: !!stripe,
          elementsReady: !!elements,
          errorMessage: error instanceof Error ? error.message : "Unknown error",
          errorStack: error instanceof Error ? error.stack : undefined,
        },
      })

      toast({
        title: "Payment Failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      })
    } finally {
      setIsProcessing(false)
    }
  }

  // Always show the form to allow users to update their payment method
  // This is useful if they entered the wrong card or want to use a different one
  return (
    <div className="mx-auto max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Vehicle Booking Payment
          </CardTitle>
          <CardDescription>
            Add your payment information to secure your vehicle booking
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              {/* Card Number */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Card Number</label>
                <div className="rounded-md border bg-white p-4">
                  <CardNumberElement
                    options={{
                      showIcon: true,
                      disableLink: true,
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#424770",
                          "::placeholder": {
                            color: "#aab7c4",
                          },
                        },
                        invalid: {
                          color: "#9e2146",
                        },
                      },
                    }}
                  />
                </div>
              </div>

              {/* Expiry and CVC in a grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Expiry Date
                  </label>
                  <div className="rounded-md border bg-white p-4">
                    <CardExpiryElement
                      options={{
                        style: {
                          base: {
                            fontSize: "16px",
                            color: "#424770",
                            "::placeholder": {
                              color: "#aab7c4",
                            },
                          },
                          invalid: {
                            color: "#9e2146",
                          },
                        },
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">CVC</label>
                  <div className="rounded-md border bg-white p-4">
                    <CardCvcElement
                      options={{
                        style: {
                          base: {
                            fontSize: "16px",
                            color: "#424770",
                            "::placeholder": {
                              color: "#aab7c4",
                            },
                          },
                          invalid: {
                            color: "#9e2146",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
                <h4 className="mb-2 font-semibold text-blue-900">Important Information</h4>
                <ul className="list-inside list-disc space-y-1 text-sm text-blue-800">
                  <li>Your card will be securely stored for vehicle-related charges</li>
                  <li>
                    All payment information is encrypted and processed securely through Stripe
                  </li>
                  <li>Your card details are never stored on our servers</li>
                  <li>
                    Charges will be automatically processed for vehicle rental and related fees
                  </li>
                </ul>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-honeycomb text-white hover:bg-amber-500"
              disabled={!stripe || isProcessing}
            >
              {isProcessing ? (
                <>
                  <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                  Processing...
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  Save Payment Method
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export default VehicleBooking
